import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { H1, H2, H3, P } from "../../../../components/Text/Text";
import Form from "../../../../components/Form/Form";
import Button from "../../../../components/Button/Button";
import { useTranslate } from "../../../../utils/hooks/useTranslate";
import { ValidatorsE } from "../../../../components/Form/yupValidations";
import { ClientTypeE } from "../../../../models/enums";
import { useRegisterClientMutation } from "../../../../api/clientAPI";
import mapPayloadForClientPost from "./helpers/mapPayloadForClientPost";
import bgImage from "../../../../assets/images/workflow_2.svg";

import styles from "./styles.module.scss";

import type { ClientT, FormFieldT } from "../../../../models/models";
import type { GetPredefinedUserDataResultT } from "../../../../api/models/models";
import { useParams } from "react-router";
import { countrySO } from "../../../../utils/helpers/selectOptions";

interface FirstStepI {
  clientData: GetPredefinedUserDataResultT;
  setCreatedClientData: Dispatch<SetStateAction<ClientT>>;
}

type UploadedImageFileT = {
  ImageFile: string;
  ImageObjectURL: string;
  ImageSource: string;
};

const FirstStep = ({ clientData, setCreatedClientData }: FirstStepI) => {
  const { token } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslate();
  const [img, setImg] = useState<UploadedImageFileT>(null);
  const [registerClient] = useRegisterClientMutation();

  const fields = useMemo(
    () =>
      [
        { label: "Company name", validation: ValidatorsE.REQUIRED_STRING },
        { label: "Contact name", validation: ValidatorsE.REQUIRED_STRING },
        { label: "Email", validation: ValidatorsE.EMAIL },
        { label: "Phone number", validation: ValidatorsE.PHONE_NUMBER },
        { label: "Address" },
        {
          // Hidden once the admin set the country while generating the link - Form filters
          // label === null out before building the yup schema, so the required rule goes too.
          label: clientData.Country ? null : "Country",
          name: "country",
          selectOptions: countrySO,
          placeholder: 'Choose your country...',
          validation: ValidatorsE.SELECT_REQUIRED,
        },
        {
          label:
            clientData.ClientType === ClientTypeE["Post/sound production"] ||
              (clientData.ClientType === ClientTypeE["Advertising Agency"] &&
                clientData.IsDeliveryService)
              ? "Name for invoice"
              : null,
          name: "InvoiceName",
          validation: ValidatorsE.REQUIRED_STRING,
        },
        {
          label:
            clientData.ClientType === ClientTypeE["Post/sound production"] ||
              (clientData.ClientType === ClientTypeE["Advertising Agency"] &&
                clientData.IsDeliveryService)
              ? "VAT/ID number"
              : null,
          name: "vat",
          validation: ValidatorsE.REQUIRED_STRING,
        },
        {
          label: "Logo image",
          type: "image",
          onChange: setImg,
        },
      ] as FormFieldT[],
    [clientData.ClientType, clientData.Country, clientData.IsDeliveryService]
  );

  const handleSubmit = useCallback(
    async (data: any) => {
      setIsSubmitting(true);

      const payload = mapPayloadForClientPost(data, clientData, img, token);

      try {
        const res = await registerClient(payload);

        if ("data" in res) {
          setCreatedClientData(res.data as unknown as ClientT);
        }
      } catch (e) {
        console.log(e);
      } finally {
        setIsSubmitting(false);
      }
    },
    [clientData, img, token, registerClient, setCreatedClientData]
  );

  const clientTypeName =
    Object.entries(ClientTypeE).find(
      (entry) => +entry[0] === +clientData.ClientType
    )?.[1] + "";

  return (
    <>
      <img src={bgImage} alt="background" className={styles.backgroundImage} />
      <div className={styles.wrapper}>
        <H1>Welcome to JUMBOmedia</H1>
        <H2>New Client Registration</H2>
        <P>
          Registering for the system includes two steps. Please follow the steps
          and fill in all the required details until you receive a registration
          confirmation.
        </P>
        <H3>First step - Account information</H3>
        <p>{t`Create a new ${clientTypeName} account`}</p>
        <hr />
        <Form
          fields={fields}
          onSubmit={handleSubmit}
          formID="registration-form"
        />
        <div className={styles.footer}>
          <Button
            form="registration-form"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Continue
          </Button>
        </div>
      </div>
    </>
  );
};

export default FirstStep;
